import { useEffect, useRef } from "react";
import { getToken } from "./auth";
import { staffStreamUrl } from "./urls";

// Live updates for the staff system. One EventSource for the whole app, shared by every component
// that calls useStaffLive(); the server sends a tiny "changed" event whenever attendance, tasks,
// notices or settings change, and each subscriber refetches straight away. If the stream can't be
// kept open (proxy, offline) — or connects but never delivers (a proxy holding events back, which
// raises no error) — it falls back to a 10s refresh until the stream proves itself with its "hello".
// Coming back to the tab refreshes, and a 60s safety refresh runs regardless, so nothing stays stale.

const subs = new Set();
let es = null;
let fallback = null;
let watchdog = null;
let safety = null;

const notify = (scope, data) => subs.forEach((fn) => fn(scope, data));

function startFallback() {
  if (!fallback) fallback = setInterval(() => notify("poll"), 10000);
}
function stopFallback() {
  if (fallback) { clearInterval(fallback); fallback = null; }
}

function open() {
  const token = getToken();
  if (!token || typeof EventSource === "undefined") { startFallback(); return; }
  es = new EventSource(`${staffStreamUrl}?token=${encodeURIComponent(token)}`);
  // the server says hello the moment the stream opens; no hello soon = events aren't getting through
  clearTimeout(watchdog);
  watchdog = setTimeout(startFallback, 6000);
  es.addEventListener("hello", () => { clearTimeout(watchdog); stopFallback(); });
  es.addEventListener("changed", (e) => {
    let msg = {};
    try { msg = JSON.parse(e.data); } catch { /* keep {} */ }
    notify(msg.scope || "", msg.data || null);
  });
  es.onerror = () => {
    // the browser retries on its own; poll meanwhile, and re-open if it gave up entirely
    startFallback();
    if (es && es.readyState === 2) {
      es = null;
      setTimeout(() => { if (subs.size && !es) open(); }, 5000);
    }
  };
}

function close() {
  if (es) { es.close(); es = null; }
  clearTimeout(watchdog);
  clearInterval(safety); safety = null;
  stopFallback();
}

export function useStaffLive(onChange, enabled = true) {
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    if (!enabled) return undefined;
    const fn = (scope, data) => cb.current && cb.current(scope, data);
    subs.add(fn);
    if (subs.size === 1) {
      open();
      safety = setInterval(() => { if (!document.hidden) notify("safety"); }, 60000);
    }
    const onVisible = () => { if (!document.hidden) fn("focus"); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      subs.delete(fn);
      document.removeEventListener("visibilitychange", onVisible);
      if (!subs.size) close();
    };
  }, [enabled]);
}
